import { useEffect } from "react";
import { Form, Input, Modal, Select, Switch } from "antd";
import {
  CmsOperatorCreateValues,
  CmsOperatorInfo,
  CmsOperatorUpdateValues,
  CmsRoleInfo,
} from "./types";

type OperatorModalProps = {
  open: boolean;
  saving: boolean;
  roles: CmsRoleInfo[];
  operator?: CmsOperatorInfo;
  onCancel: () => void;
  onCreate: (values: CmsOperatorCreateValues) => Promise<boolean>;
  onUpdate: (
    keycloakId: string,
    values: CmsOperatorUpdateValues
  ) => Promise<boolean>;
};

type FormValues = {
  login: string;
  password?: string;
  fullName?: string;
  description?: string;
  roleId?: number;
  enabled?: boolean;
};

export const OperatorModal = ({
  open,
  saving,
  roles,
  operator,
  onCancel,
  onCreate,
  onUpdate,
}: OperatorModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const isEdit = Boolean(operator);

  useEffect(() => {
    if (!open) {
      return;
    }
    form.setFieldsValue({
      login: operator?.login || "",
      password: "",
      fullName: operator?.fullName || "",
      description: operator?.description || "",
      roleId: operator?.roleId ?? undefined,
      enabled: operator ? operator.enabled : true,
    });
  }, [form, open, operator]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const ok = operator
      ? await onUpdate(operator.keycloakId, {
          login: values.login,
          password: values.password || undefined,
          fullName: values.fullName,
          description: values.description,
          roleId: values.roleId ?? null,
          enabled: values.enabled,
        })
      : await onCreate({
          login: values.login,
          password: values.password || "",
          fullName: values.fullName,
          description: values.description,
          roleId: values.roleId,
        });
    if (ok) {
      form.resetFields();
    }
  };

  return (
    <Modal
      title={isEdit ? "Редактировать пользователя" : "Создать пользователя"}
      open={open}
      okText="Сохранить"
      cancelText="Отмена"
      confirmLoading={saving}
      onOk={handleSubmit}
      onCancel={onCancel}
      destroyOnClose
    >
      <Form<FormValues> form={form} layout="vertical">
        <Form.Item
          name="fullName"
          label="ФИО"
          rules={[{ required: !isEdit, message: "Введите ФИО" }]}
        >
          <Input placeholder="Иванов Иван Иванович" />
        </Form.Item>

        <Form.Item name="description" label="Описание">
          <Input.TextArea
            rows={2}
            placeholder="Должность, зона ответственности"
          />
        </Form.Item>

        <Form.Item
          name="login"
          label="Логин"
          rules={[{ required: true, message: "Введите логин" }]}
        >
          <Input placeholder="ivanov" autoComplete="off" />
        </Form.Item>

        <Form.Item
          name="password"
          label={isEdit ? "Новый пароль" : "Пароль"}
          rules={[{ required: !isEdit, message: "Введите пароль" }]}
          extra={isEdit ? "Оставьте пустым, чтобы не менять" : undefined}
        >
          <Input.Password placeholder="••••••••" autoComplete="new-password" />
        </Form.Item>

        <Form.Item name="roleId" label="Роль">
          <Select
            allowClear
            placeholder="Без роли (нет доступа к разделам)"
            options={roles.map((role) => ({
              label: role.name,
              value: role.id,
            }))}
          />
        </Form.Item>

        {isEdit && (
          <Form.Item name="enabled" label="Активен" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
